import { useState } from 'react';

import { TrackingEvents, TrackingTabs, useTrack } from '../../tracking';
import type { Option } from '@/common/types';
import { Filter } from '@/ui/components';

import { TeamFilterOptionValues } from '../../constants';
import type { TeamFilterOption } from '../../types';

import type { LatestGame } from './LastFiveGames.types';
import GameRow from './GameRow/GameRow';
import NoGamesInfo from './GameRow/NoGamesInfo';

interface LastFiveGamesProps {
  /** Name of the home team */
  homeTeamName: string;
  /** Name of the away team */
  awayTeamName: string;
  /** Array of the latest games played by the home team */
  latestHomeGames: LatestGame[];
  /** Array of the latest games played by the away team */
  latestAwayGames: LatestGame[];
}

const MAX_GAMES_TO_DISPLAY = 5;

export default function LastFiveGames({
  homeTeamName,
  awayTeamName,
  latestHomeGames,
  latestAwayGames,
}: LastFiveGamesProps) {
  const track = useTrack();
  const [filterValue, setFilterValue] = useState<TeamFilterOption>(
    TeamFilterOptionValues.HOME_TEAM,
  );

  const filterOptions: Option<TeamFilterOption>[] = [
    { label: homeTeamName, value: TeamFilterOptionValues.HOME_TEAM },
    { label: awayTeamName, value: TeamFilterOptionValues.AWAY_TEAM },
  ];

  const handleFilterChange = (value: TeamFilterOption) => {
    track(TrackingEvents.FilterClick, {
      tab: TrackingTabs.Stats,
      filter: value,
    });
    setFilterValue(value);
  };

  const gamesToDisplay =
    filterValue === TeamFilterOptionValues.HOME_TEAM ? latestHomeGames : latestAwayGames;
  const limitedGamesToDisplay = gamesToDisplay.slice(0, MAX_GAMES_TO_DISPLAY);

  return (
    <div className="flex flex-col">
      <div className="p-2">
        <Filter options={filterOptions} value={filterValue} onValueChange={handleFilterChange} />
      </div>
      <div className="flex flex-col">
        {limitedGamesToDisplay.length === 0 && <NoGamesInfo />}
        {limitedGamesToDisplay.map(
          ({
            date,
            league,
            leagueFlag,
            stage,
            stageNumber,
            homeTeam,
            awayTeam,
            homeScore,
            awayScore,
            homePenaltyScore,
            awayPenaltyScore,
            result,
            homeGoalScorers,
            awayGoalScorers,
          }) => (
            <GameRow
              key={`${date}-${league}-${homeTeam}-${awayTeam}`}
              date={date}
              league={league}
              leagueFlag={leagueFlag}
              stage={stage}
              stageNumber={stageNumber}
              homeTeam={homeTeam}
              awayTeam={awayTeam}
              homeScore={homeScore}
              awayScore={awayScore}
              homePenaltyScore={homePenaltyScore}
              awayPenaltyScore={awayPenaltyScore}
              result={result}
              homeGoalScorers={homeGoalScorers}
              awayGoalScorers={awayGoalScorers}
            />
          ),
        )}
      </div>
    </div>
  );
}
